import type { PergolaConfig } from '../config/types';
import type { QuoteResult } from '../config/quote';
import type { WizardState } from '../config/schema';

/** Presupuesto serializado (config + dimensiones mm + partidas + total + meta). */
export interface QuotePayload {
  quoteId: string;
  timestamp: string;
  /** Selecciones crudas del wizard. */
  config: WizardState;
  dimensions: { widthMm: number; lengthMm: number; heightMm: number };
  /** Configuración resuelta usada por el 3D/CPQ. */
  resolved: PergolaConfig;
  pricing: QuoteResult;
  /** Imagen-resumen (dataURL) adjunta al POST. */
  image?: string;
  meta: { app: string; version: string };
}

export interface SaveResult {
  ok: boolean;
  via: 'server' | 'local';
  message: string;
}

const LS_KEY = 'teyco_quotes';

/** Construye el payload estructurado del presupuesto. */
export function buildQuotePayload(args: {
  state: WizardState;
  config: PergolaConfig;
  quote: QuoteResult;
  quoteId: string;
  image?: string;
}): QuotePayload {
  return {
    quoteId: args.quoteId,
    timestamp: new Date().toISOString(),
    config: args.state,
    dimensions: {
      widthMm: args.config.widthMm,
      lengthMm: args.config.lengthMm,
      heightMm: args.config.heightMm,
    },
    resolved: args.config,
    pricing: args.quote,
    image: args.image,
    meta: { app: 'teyco-pergola-configurator', version: '0.1.0' },
  };
}

/**
 * Guarda el presupuesto: descarga local del JSON + POST a /api/quotes.
 * Si el backend no responde, hace fallback a localStorage sin romperse.
 */
export async function saveQuote(payload: QuotePayload): Promise<SaveResult> {
  // 1) Descarga local (JSON legible, sin la imagen embebida).
  const { image: _omit, ...recordNoImage } = payload;
  void _omit;
  downloadJSON(recordNoImage, `${payload.quoteId}.json`);

  // 2) POST al backend (con la imagen adjunta).
  try {
    const res = await fetch('/api/quotes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as { quoteId?: string };
    return {
      ok: true,
      via: 'server',
      message: `Presupuesto guardado en el servidor (${data.quoteId ?? payload.quoteId}) y descargado.`,
    };
  } catch {
    // 3) Fallback: localStorage (sin imagen) + descarga ya realizada.
    saveLocal(recordNoImage);
    return {
      ok: true,
      via: 'local',
      message: 'Backend no disponible: presupuesto descargado y guardado localmente (localStorage).',
    };
  }
}

function saveLocal(record: Omit<QuotePayload, 'image'>): void {
  try {
    const list = JSON.parse(localStorage.getItem(LS_KEY) ?? '[]') as unknown[];
    list.unshift(record);
    localStorage.setItem(LS_KEY, JSON.stringify(list.slice(0, 20)));
  } catch (err) {
    console.warn('[Teyco] No se pudo guardar en localStorage:', err);
  }
}

function downloadJSON(obj: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
